import React from 'react'

export interface MultipleChoiceWidgetProps {
  options: string[]
  value: string[]
  onChange: (value: string[]) => void
  minSelections?: number
  maxSelections?: number
  disabled?: boolean
}

export function MultipleChoiceWidget({
  options,
  value,
  onChange,
  maxSelections,
  disabled = false,
}: MultipleChoiceWidgetProps): React.ReactElement {
  const isMaxReached =
    maxSelections !== undefined && value.length >= maxSelections

  function handleToggle(option: string): void {
    if (disabled) return

    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option))
    } else if (!isMaxReached) {
      onChange([...value, option])
    }
  }

  return (
    <div
      role="group"
      aria-label="Select one or more options"
      data-testid="multiple-choice-widget"
      className="flex flex-col gap-2"
    >
      {options.map((option) => {
        const isChecked = value.includes(option)
        const isOptionDisabled = disabled || (!isChecked && isMaxReached)

        return (
          <label
            key={option}
            className={[
              'flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium font-sans transition-colors',
              'focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-2',
              isChecked
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-neutral-300 bg-neutral-0 text-neutral-700 hover:border-brand-300 hover:bg-neutral-50',
              isOptionDisabled
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer',
            ].join(' ')}
            data-testid={`multiple-choice-option-${option}`}
          >
            <input
              type="checkbox"
              value={option}
              checked={isChecked}
              onChange={() => handleToggle(option)}
              disabled={isOptionDisabled}
              className="sr-only"
              aria-checked={isChecked}
            />
            <span
              aria-hidden="true"
              className={[
                'flex items-center justify-center w-5 h-5 rounded-md border-2 shrink-0 transition-colors',
                isChecked
                  ? 'border-brand-500 bg-brand-500'
                  : 'border-neutral-400 bg-neutral-0',
              ].join(' ')}
            >
              {isChecked && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M2.5 6L5 8.5L9.5 3.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-neutral-0"
                  />
                </svg>
              )}
            </span>
            <span>{option}</span>
          </label>
        )
      })}
    </div>
  )
}
